/**
 * components/quotes/Arrowhead.tsx — Punta di freccia di una quota (MUTA).
 *
 * Cono orientato con `setFromUnitVectors` (stesso trucco di
 * `Fastener.tsx`/`ConstraintDebug.tsx`): la punta del cono (locale +Y/2)
 * atterra esattamente su `tip`, il resto del cono si estende all'indietro
 * lungo `direction`. Condiviso da `LinearDimension`/`AngularDimension`
 * (quote, dimensioni da `QUOTE_DEFAULTS` — default se `lengthMm`/`widthMm`
 * assenti) e da `ForceArrow` (carichi applicati, dimensioni da
 * `FORCE_ARROW_DEFAULTS`, piu' grandi — 28-09-2026).*/

import { useMemo } from "react";
import * as THREE from "three";
import { QUOTE_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";

export interface ArrowheadProps {
  /** Punto mondo dove deve atterrare la punta del cono.*/
  tip: Vec3;
  /** Direzione (non necessariamente unitaria) verso cui punta la freccia.*/
  direction: Vec3;
  color: string;
  /** Lunghezza del cono (mm); default `QUOTE_DEFAULTS.arrowLengthMm`.*/
  lengthMm?: number;
  /** Diametro del cono (mm); default `QUOTE_DEFAULTS.arrowWidthMm`.*/
  widthMm?: number;
}

const UNIT_Y = new THREE.Vector3(0, 1, 0);

/** Piccolo cono di terminazione (quota o freccia di carico), dimensioni configurabili.*/
export function Arrowhead({
  tip,
  direction,
  color,
  lengthMm = QUOTE_DEFAULTS.arrowLengthMm,
  widthMm = QUOTE_DEFAULTS.arrowWidthMm,
}: ArrowheadProps) {
  const { position, quaternion } = useMemo(() => {
    const dir = new THREE.Vector3(...direction);
    //Direzione degenere → identita' (fallback verticale), come Fastener.tsx.
    if (dir.lengthSq() < 1e-12) dir.set(0, 1, 0);
    dir.normalize();

    const quat = new THREE.Quaternion().setFromUnitVectors(UNIT_Y, dir);
    const half = lengthMm / 2;
    const tipVec = new THREE.Vector3(...tip);
    const pos = tipVec.clone().sub(dir.clone().multiplyScalar(half));
    return { position: [pos.x, pos.y, pos.z] as Vec3, quaternion: quat };
  }, [tip, direction, lengthMm]);

  return (
    <mesh position={position} quaternion={quaternion}>
      <coneGeometry args={[widthMm / 2, lengthMm, QUOTE_DEFAULTS.arrowConeSegments]} />
      <meshBasicMaterial color={color} />
    </mesh>
  );
}
